import { useState, useRef, useEffect } from "react";
import { CommentColor, CommentModel } from "@/types";
import { useTranslation } from "react-i18next";
import Comment from "./Comment";
import { usePlayer } from "@/lib/PlayerContext";
import { BsPinAngleFill } from "react-icons/bs";

// URLを検出してリンク化する関数（25文字以上は省略表示）
const renderMessageWithLinks = (message: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = message.split(urlRegex);
  
  return parts.map((part, index) => {
    // URLかどうかを判定（http:// または https:// で始まるか）
    if (part.match(/^https?:\/\//)) {
      const displayText = part.length > 25 ? part.substring(0, 25) + '...' : part;
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="tw-text-blue-300 tw-underline hover:tw-text-blue-200"
        >
          {displayText}
        </a>
      );
    }
    // 通常のテキストの場合
    return part;
  });
};

export interface CommentViewProps {
  comments: CommentModel[];
}

const CommentView = ({ comments }: CommentViewProps) => {
  const { commentScrollEnabled, pinnedComment, showComment } = usePlayer();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [prevCommentCount, setPrevCommentCount] = useState(0);

  const scrollBottom = (smooth = true) => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: smooth ? "smooth" : "instant",
      });
    }
  };

  // コメント数の変化を監視
  useEffect(() => {
    const currentCommentCount = comments.length;

    if (currentCommentCount !== prevCommentCount) {
      // コメント数が変化した場合
      if (commentScrollEnabled) {
        // スクロール機能が有効な場合はスムーズにスクロール
        setTimeout(() => {
          scrollBottom(true);
        }, 50);
      } else {
        // 従来の動作
        setTimeout(() => {
          scrollBottom(false);
        }, 100);
      }
      setPrevCommentCount(currentCommentCount);
    }
  }, [comments, commentScrollEnabled, prevCommentCount]);

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <div className="tw-h-full tw-flex tw-flex-col">
        {/* 通常コメント一覧（上部に押し上げられる） */}
        <div
          ref={scrollContainerRef}
          className="tw-pl-2 tw-pr-4 tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-pointer-events-auto tw-hidden-scrollbar"
          style={{
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            pointerEvents: comments.length > 0 || pinnedComment ? "auto" : "none",
          }}
        >
          <div className="tw-grid tw-grid-cols-1 tw-gap-3 tw-w-[90%] tw-max-w-[350px] tw-pr-4">
            {comments.map((comment, index) => {
              const isNewComment =
                commentScrollEnabled && index >= prevCommentCount - 1;
              return (
                <div
                  key={index}
                  className={isNewComment ? "tw-animate-fade-in" : ""}
                  style={{
                    animation: isNewComment
                      ? "fadeIn 0.3s ease-in-out"
                      : undefined,
                  }}
                >
                  <Comment
                    id={comment.id}
                    name={comment.name}
                    message={comment.message}
                    createdAt={comment.createdAt}
                    backgroundColor={comment.backgroundColor as CommentColor}
                    originalLanguage={comment.originalLanguage}
                  />
                </div>
              );
            })}
          </div>
        </div>
        
        {/* ピン留めコメント表示エリア（下部に表示、ボタンの上） */}
        {pinnedComment && showComment && comments.length > 0 && (
          <div
            className="tw-flex-shrink-0 tw-ml-2 tw-mr-4 tw-mb-1 tw-bg-amber-500/30 tw-backdrop-blur-sm tw-rounded-lg tw-p-1.5 tw-border tw-border-amber-400/50 tw-w-[90%] tw-max-w-[350px] tw-pointer-events-auto"
            style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            <div className="tw-flex tw-items-center tw-gap-1 tw-mb-0.5">
              <BsPinAngleFill className="tw-text-amber-300 tw-text-sm" />
              <span 
                className="tw-text-white tw-text-xs tw-font-bold"
                style={{ 
                  textShadow: '1px 1px 2px rgba(64, 64, 64, 0.7), -1px -1px 2px rgba(64, 64, 64, 0.7)' 
                }}
              >
                {pinnedComment.name}
              </span>
            </div>
            <div
              className="tw-px-1.5 tw-py-0.5 tw-whitespace-pre-wrap tw-text-sm tw-text-left tw-text-white"
              style={{ 
                textShadow: '1px 1px 2px rgba(64, 64, 64, 0.7), -1px -1px 2px rgba(64, 64, 64, 0.7)' 
              }}
            >
              {renderMessageWithLinks(pinnedComment.message)}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CommentView;
